import { useGoogleAnalyticsContext } from "contexts/GoogleAnalyticsProvider";
import { useWebStoreContext } from "contexts/WebStoreProvider";
import { useAppDispatch, useAppSelector } from "hooks";
import { Trigger } from "interfaces";
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { onDIPtrigger, selectDipState, sendDipAction } from "redux/slices";
import { dispatchLogOut, getDipAnalyticsType } from "../helper";
import { useLinks } from "hooks/useLinks";

export const useListeners = (): void => {
	const dispatch = useAppDispatch();
	const { handleErrorResponse, logout } = useWebStoreContext();
	const {
		currentDip,
		claimRewardProcess,
		fromHud,
		activeTrigger,
		availableTriggers,
		pendingData
	} = useAppSelector(selectDipState);
	const { reportEvent, reportPlaygamiEvent } = useGoogleAnalyticsContext();
	const { pathname } = useLocation();
	const links = useLinks();
	const lastTriggered = useRef<Trigger>();

	const PATHS_DIPS_TRIGGERS: Array<[string, Trigger]> = [
		[links.shop.path, "mainShop"],
		[links.loyalty.path, "loyaltyShop"],
		[links.free_daily_gifts.path, "freeDailyGifts"],
		[links.club_free_gift.path, "clubFreeGift"],
		[links.club_shop.path, "clubShop"],
		[links.pois_bounty.path, "poisBounty"],
		[links.drip_offer.path, "dripOffer"],
		[links.elite_deals.path, "eliteDeals"],
		[`${links.friend_center.path}/shop`, "friendCenterShop"],
		[`${links.friend_center.path}/invite`, "friendReferral"]
	];
	const trigger = PATHS_DIPS_TRIGGERS.find(
		([path]) => pathname === path || pathname.startsWith(`${path}/`)
	)?.[1];

	/**🔦 Handling errors */
	useEffect(() => {
		const { error } = claimRewardProcess;
		if (error) {
			handleErrorResponse("Request Failed");
		}
	}, [claimRewardProcess]);

	/**🔦 sendDipAction when the DIP component init and/or change */
	useEffect(() => {
		if (!currentDip) {
			return;
		}
		document.querySelector("body")?.classList.add("stop-scroll");

		const { buttons: data } = currentDip;

		dispatch(sendDipAction({ actionType: "view" }));
		reportEvent("dialog_shown", {
			source: "dips_dialog",
			dip_id: currentDip.dip.id,
			page_location: pathname,
			offers: JSON.stringify(
				data?.map((offer) => {
					return {
						loyalty_offer_id: offer.loyaltyOfferId,
						reward_id: offer.rewardId,
						offer_id: offer.offerId
					};
				})
			)
		});
		reportPlaygamiEvent("tp.web_frontend", {
			event_name: fromHud ? "limited_deal_hud_show" : "dialog_shown",
			event_type: "dips_dialog",
			action: "dialog_shown",
			source: "dips_dialog",
			event_id: currentDip.dip.id,
			offer_id: currentDip.buttons?.map((b) => b.offerId).join(", "),
			type: getDipAnalyticsType({
				fromHud,
				activeTrigger,
				pathname,
				shopPath: links.shop.path,
				eliteDealsPath: links.elite_deals.path
			}),
			page_location: pathname
		});

		return () => {
			if (dispatchLogOut(currentDip)) {
				logout();
			}
			document.querySelector("body")?.classList.remove("stop-scroll");
		};
	}, [currentDip?.dip?.id]);

	useEffect(() => {
		if (!trigger) {
			lastTriggered.current = undefined;
			return;
		}
		if (
			pendingData ||
			!availableTriggers.includes(trigger) ||
			lastTriggered.current === trigger
		) {
			return;
		}

		lastTriggered.current = trigger;
		dispatch(onDIPtrigger({ triggerType: trigger }));
	}, [pathname, trigger, pendingData, availableTriggers, dispatch]);
};
